import { useState, useMemo, useEffect, useRef } from 'react'
import {
  TableCellsIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ChartPieIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline'
import { QueryRow } from './types'
import { ItemList, Item } from '../ItemList'
import { ChallengeService } from '@duck/api-definitions'
import { Button } from '@duck/ui'

import { useEventEmitter } from '@modules/EventBusModule'
import { v4 as uuidv4 } from 'uuid'
import { TranslationKey, useTar } from '@/modules/i18n'
import { renderChart } from '@/utils/chartRenderer'

interface GenerateDataChartProps {
  queryResults: QueryRow[]
  query: string
  onSubmit: () => void
}

const CHART_TYPES: Record<ChartType, { label: TranslationKey, icon: React.ReactNode }> = {
  table: { label: 'common.table', icon: <TableCellsIcon className="w-4 h-4" /> },
  line: { label: 'common.lineChart', icon: <PresentationChartLineIcon className="w-4 h-4" /> },
  bar: { label: 'common.barChart', icon: <ChartBarIcon className="w-4 h-4" /> },
  pie: { label: 'common.pieChart', icon: <ChartPieIcon className="w-4 h-4" /> },
  scatter: { label: 'common.scatterChart', icon: <Squares2X2Icon className="w-4 h-4" /> }
} as const

type ChartType = ChallengeService.Chart['chartType']


const chartTypeItems: Item[] = Object.entries(CHART_TYPES).map(([key, config]) => ({
  value: key,
  label: config.label,
  icon: config.icon
}))

export const GenerateDataChart = ({ queryResults, query, onSubmit }: GenerateDataChartProps) => {
  const [xAxis, setXAxis] = useState<string>('')
  const [yAxis, setYAxis] = useState<string>('')
  const [chartType, setChartType] = useState<ChartType>('table')
  const [showTrendLine, setShowTrendLine] = useState<boolean>(false)
  const { t } = useTar()
  const chartPreviewRef = useRef<HTMLDivElement>(null)

  const emitChartAdded = useEventEmitter('chart:added')

  // Extract column names from the first row
  const columns = queryResults.length > 0 ? Object.keys(queryResults[0]) : []

  // Create column items for the list
  const columnItems: Item[] = useMemo(() =>
    columns.map(column => ({
      value: column,
      label: column
    })), [columns])

  // Render chart preview when configuration changes
  useEffect(() => {
    if (!chartPreviewRef.current || queryResults.length === 0) {
      return
    }

    // Create a preview chart configuration
    const previewChart: ChallengeService.Chart = {
      id: 'preview',
      chartType,
      xAxis,
      yAxis,
      query,
      data: queryResults,
      showTrendLine: chartType === 'scatter' ? showTrendLine : undefined
    }

    // Render the chart and store the cleanup function
    const cleanup = renderChart(previewChart, chartPreviewRef.current, true)

    // Return cleanup function to dispose chart on unmount or update
    return cleanup
  }, [chartType, xAxis, yAxis, queryResults, query, showTrendLine])

  const handleSubmit = () => {
    const chartId = uuidv4().slice(0, 8)
    const chart: ChallengeService.Chart = {
      id: chartId,
      chartType,
      xAxis,
      yAxis,
      query,
      data: queryResults,
      showTrendLine: chartType === 'scatter' ? showTrendLine : undefined
    }
    emitChartAdded({
      chart
    })
    onSubmit()
  }

  const isSubmitDisabled = queryResults.length === 0 ||
    (chartType !== 'table' && (!xAxis || !yAxis))

  if (queryResults.length === 0) {
    return null
  }

  return (
    <div className="space-y-6 p-4 bg-card border border-border rounded-sm max-w-[846px]">
      <div className="space-y-6">
        {/* Chart Preview */}
        <div
          ref={chartPreviewRef}
          className="w-full h-64 bg-background border border-border rounded-sm overflow-auto custom-scrollbar"
        />

        <div className={`grid gap-6 grid-cols-1 md:grid-cols-3`}>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              {t('common.chartType')}
            </label>
            <ItemList
              items={chartTypeItems.map(item => ({ ...item, label: t(item.label as TranslationKey) }))}
              onChange={(item) => setChartType(item.value as ChartType)}
              defaultValue={"table"}
            />
            {chartType === 'scatter' && (
              <div className="mt-2 pt-2 pl-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowTrendLine(!showTrendLine)}
                  className="flex items-center justify-between w-full text-sm text-foreground hover:text-accent transition-colors"
                >
                  <span>{t('common.showTrendLine')}</span>
                  <div
                    className={`
                      relative inline-flex h-5 w-9 items-center rounded-full transition-colors
                      ${showTrendLine ? 'bg-accent' : 'bg-muted'}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${showTrendLine ? 'translate-x-5' : 'translate-x-1'}
                      `}
                    />
                  </div>
                </button>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <label className={`block text-sm font-medium ${chartType === 'table' ? 'text-muted' : 'text-foreground'}`}>
              {t('common.xAxis')}
            </label>
            <ItemList
              items={columnItems}
              onChange={(item) => setXAxis(item.value)}
              disabled={chartType === 'table'}
            />
          </div>

          {/* Y Axis Selection */}
          <div className="space-y-3">
            <label className={`block text-sm font-medium ${chartType === 'table' ? 'text-muted' : 'text-foreground'}`}>
              {t('common.yAxis')}
            </label>
            <ItemList
              items={columnItems}
              onChange={(item) => setYAxis(item.value)}
              disabled={chartType === 'table'}
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-between pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={onSubmit}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="default"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {t('common.insertToSolution')}
        </Button>
      </div>
    </div>
  )
}